import { useState, useEffect } from 'react';

/**
 * Hook to determine if a CSS media query matches.
 * Prevents server-side rendering (SSR) issues.
 * 
 * @param {string} query - A valid media query string (e.g. "(max-width: 768px)")
 * @returns {boolean} Whether the media query currently matches
 */
export function useMediaQuery(query) {
  const getMatches = (q) => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(q).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState(getMatches(query));

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    const handleChange = () => setMatches(matchMedia.matches);

    // Initial check
    handleChange();

    // Add listener
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener('change', handleChange);
    } else {
      matchMedia.addListener(handleChange); // Legacy fallback
    }

    // Cleanup
    return () => {
      if (matchMedia.removeEventListener) {
        matchMedia.removeEventListener('change', handleChange);
      } else {
        matchMedia.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
}
